// Insulation SF ↔ bags conversion.
//
// Batting is sold by the bag, each bag covering `coverageSf` square feet
// (parsed from the SKU name "(N SF/bag)", migration 0060). The office estimates
// the actual SF a job needs; materials are purchased in WHOLE bags, so we round
// UP. Labor, separately, charges on the actual SF (never the rounded-up bags).
//
// Example: 70 SF needed, 30 SF/bag → ceil(70/30) = 3 bags.

/**
 * Whole bags needed to cover `sf` square feet at `coverageSf` SF per bag.
 * Returns 0 for a non-positive SF; throws on a non-positive coverage (a SKU
 * with no/zero coverage_sf is not a batting SKU and must not reach here).
 */
export function bagsFromSf(sf: number, coverageSf: number): number {
  if (!Number.isFinite(coverageSf) || coverageSf <= 0) {
    throw new Error(`bagsFromSf: coverageSf must be > 0, got ${coverageSf}`);
  }
  if (!Number.isFinite(sf) || sf <= 0) return 0;
  return Math.ceil(sf / coverageSf);
}

/**
 * Resolve a line's WHOLE-bag engine quantity from its entered SF, given the
 * SKU's coverage_sf. The per-line `coverageSfOverride` (migration 0074) — used
 * by shared-bag presets like KN3646 whose SKU coverage_sf is null — takes
 * precedence over the SKU coverage. Returns `null` when the line isn't an
 * SF-driven insulation line (no enteredSf, or no coverage resolves), signalling
 * the caller to fall back to the raw line quantity.
 *
 * SINGLE SOURCE OF TRUTH for SF→bags resolution. The quote-save route, the
 * pricing-preview route, and (read-side) jobs-list all price insulation through
 * this; they MUST stay in lockstep — a divergence here mis-prices preview vs
 * save (the 2026-06-28 KN3646 bug, where only the save route had the override
 * fallback). Do not re-implement inline.
 */
export function enteredSfToBags(
  li: { enteredSf?: number | null; coverageSfOverride?: number | null },
  skuCoverageSf: number | null,
): number | null {
  if (li.enteredSf == null) return null;
  const cov = li.coverageSfOverride ?? skuCoverageSf ?? null;
  return cov && cov > 0 ? bagsFromSf(li.enteredSf, cov) : null;
}
